// Sistema de shortcodes para el blog dinámico.
//
// Sintaxis: bloques delimitados por `:::nombre [params]` ... `:::` en líneas
// propias. También admite self-closing en una sola línea: `:::nombre params:::`.
// Allowlist cerrada — debe coincidir con backend/blog/shortcodesSpec.js.
//
// Exporta:
//   - parseContenido(md) → array de bloques { type: 'md' | 'shortcode', ... }
//   - <MarkdownContent contenido={md} /> → componente que orquesta el render
//
// El parser es propio (sin remark-directive) para evitar 50 KB extra de
// dependencias y mantener control total sobre los edge cases.

import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Link } from 'react-router-dom'
import {
  FiInfo, FiAlertTriangle, FiCheckCircle, FiZap, FiBookOpen,
  FiArrowRight, FiPlay, FiChevronDown, FiChevronUp,
} from 'react-icons/fi'
import { useState } from 'react'

// ─── Parser ─────────────────────────────────────────────────────

const OPEN_RE   = /^:::([a-z][a-z0-9-]*)(?:\s+(.*))?$/
const CLOSE_RE  = /^:::$/
const SELF_LINE = /^:::([a-z][a-z0-9-]*)(?:\s+(.*))?:::$/

// Parsea params estilo HTML: clave="valor con espacios" clave2="otro".
function parseParams(raw) {
  const out = {}
  if (!raw) return out
  const re = /([a-z][a-z0-9-]*)\s*=\s*"([^"]*)"/g
  let m
  while ((m = re.exec(raw))) out[m[1]] = m[2]
  return out
}

export function parseContenido(md) {
  if (!md) return []
  const lines = md.split('\n')
  const blocks = []
  let buffer = []
  let i = 0

  const flushMd = () => {
    if (buffer.length) {
      const content = buffer.join('\n').trim()
      if (content) blocks.push({ type: 'md', content })
      buffer = []
    }
  }

  while (i < lines.length) {
    const line = lines[i]

    // Self-closing: :::nombre params:::
    const selfMatch = line.match(SELF_LINE)
    if (selfMatch) {
      flushMd()
      blocks.push({
        type: 'shortcode',
        name: selfMatch[1],
        params: parseParams(selfMatch[2] || ''),
        body: null,
      })
      i++
      continue
    }

    // Multi-line: :::nombre params  (open)
    const openMatch = line.match(OPEN_RE)
    if (openMatch) {
      flushMd()
      const name = openMatch[1]
      const params = parseParams(openMatch[2] || '')
      const bodyLines = []
      i++
      while (i < lines.length && !CLOSE_RE.test(lines[i])) {
        bodyLines.push(lines[i])
        i++
      }
      i++ // consume :::
      blocks.push({ type: 'shortcode', name, params, body: bodyLines.join('\n') })
      continue
    }

    buffer.push(line)
    i++
  }

  flushMd()
  return blocks
}

// ─── Componentes shortcode ──────────────────────────────────────

// Sustituye el componente <a> de react-markdown para que enlaces internos
// (empiezan por "/") usen <Link> y NO recarguen la página.
const mdComponents = {
  a: ({ href, children, ...rest }) => {
    if (href && href.startsWith('/')) {
      return <Link to={href} {...rest}>{children}</Link>
    }
    return <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>{children}</a>
  },
}

// Markdown inline (para body de callouts, quotes...): hereda los componentes
// pero se renderiza dentro de un wrapper más pequeño.
function InlineMarkdown({ children }) {
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>
      {children || ''}
    </ReactMarkdown>
  )
}

const CALLOUT_STYLES = {
  info:    { bg: 'bg-blue-50',    border: 'border-blue-200',    text: 'text-blue-900',    icon: FiInfo,         iconColor: 'text-blue-600' },
  warning: { bg: 'bg-amber-50',   border: 'border-amber-200',   text: 'text-amber-900',   icon: FiAlertTriangle, iconColor: 'text-amber-600' },
  success: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-900', icon: FiCheckCircle,  iconColor: 'text-emerald-600' },
  tip:     { bg: 'bg-indigo-50',  border: 'border-indigo-200',  text: 'text-indigo-900',  icon: FiZap,          iconColor: 'text-indigo-600' },
  legal:   { bg: 'bg-gray-50',    border: 'border-gray-300',    text: 'text-gray-900',    icon: FiBookOpen,     iconColor: 'text-gray-700' },
}

function Callout({ params, body }) {
  const style = CALLOUT_STYLES[params.tipo] || CALLOUT_STYLES.info
  const Icon = style.icon
  return (
    <aside className={`my-6 rounded-2xl border ${style.border} ${style.bg} p-5 not-prose`}>
      <div className="flex items-start gap-3">
        <Icon className={`flex-shrink-0 h-5 w-5 mt-0.5 ${style.iconColor}`} />
        <div className={`flex-1 ${style.text}`}>
          {params.titulo && <div className="font-bold mb-1.5 text-sm">{params.titulo}</div>}
          <div className="text-sm leading-relaxed prose-sm prose-p:my-1.5 prose-p:text-current">
            <InlineMarkdown>{body}</InlineMarkdown>
          </div>
        </div>
      </div>
    </aside>
  )
}

function CTABox({ params }) {
  const variante = params.variante || 'primario'
  const isInternal = params['boton-url']?.startsWith('/')
  const ButtonEl = isInternal ? Link : 'a'
  const buttonProps = isInternal
    ? { to: params['boton-url'] }
    : { href: params['boton-url'], target: '_blank', rel: 'noopener noreferrer' }

  if (variante === 'secundario') {
    return (
      <div className="my-10 rounded-2xl border border-gray-200 bg-white p-6 not-prose">
        <h3 className="text-lg font-bold text-gray-900 mb-1">{params.titulo}</h3>
        {params.descripcion && (
          <p className="text-sm text-gray-600 mb-4 leading-relaxed">{params.descripcion}</p>
        )}
        <ButtonEl
          {...buttonProps}
          className="inline-flex items-center gap-1.5 text-blue-600 hover:text-blue-700 font-semibold text-sm"
        >
          {params['boton-texto']} <FiArrowRight className="h-3.5 w-3.5" />
        </ButtonEl>
      </div>
    )
  }

  return (
    <div className="my-10 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-700 p-8 text-center not-prose">
      <h3 className="text-2xl font-bold text-white mb-2">{params.titulo}</h3>
      {params.descripcion && (
        <p className="text-blue-100 mb-6 max-w-2xl mx-auto">{params.descripcion}</p>
      )}
      <ButtonEl
        {...buttonProps}
        className="inline-flex items-center gap-2 bg-white text-blue-700 font-bold py-3 px-8 rounded-xl text-base shadow-xl hover:bg-blue-50 transition-all"
      >
        {params['boton-texto']} <FiArrowRight className="h-4 w-4" />
      </ButtonEl>
    </div>
  )
}

function Comparativa({ body }) {
  return (
    <div className="my-8 overflow-x-auto rounded-2xl border border-gray-200 not-prose">
      <div className="prose-sm prose-table:my-0 prose-table:w-full prose-th:bg-gray-50 prose-th:font-semibold prose-th:text-gray-900 prose-th:px-4 prose-th:py-3 prose-th:text-sm prose-td:px-4 prose-td:py-3 prose-td:text-sm prose-td:border-t prose-td:border-gray-100">
        <ReactMarkdown remarkPlugins={[remarkGfm]} components={mdComponents}>{body}</ReactMarkdown>
      </div>
    </div>
  )
}

function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false)
  return (
    <div className="border-b border-gray-100 last:border-b-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start justify-between gap-4 py-4 text-left hover:bg-gray-50 transition-colors"
      >
        <span className="font-semibold text-gray-900 text-base">{q}</span>
        {open ? <FiChevronUp className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" /> : <FiChevronDown className="h-5 w-5 text-gray-400 flex-shrink-0 mt-0.5" />}
      </button>
      {open && (
        <div className="pb-4 text-sm text-gray-600 leading-relaxed prose prose-sm max-w-none prose-p:my-1.5">
          <InlineMarkdown>{a}</InlineMarkdown>
        </div>
      )}
    </div>
  )
}

// Body del FAQ: alternancia P:/R: por línea (admite multi-línea por respuesta).
// Líneas que empiezan por "P:" abren pregunta; las demás se acumulan en respuesta.
function parseFaqBody(body) {
  const items = []
  if (!body) return items
  const lines = body.split('\n')
  let cur = null
  for (const line of lines) {
    const mP = line.match(/^P:\s*(.+)$/)
    const mR = line.match(/^R:\s*(.+)$/)
    if (mP) {
      if (cur) items.push(cur)
      cur = { q: mP[1].trim(), a: '' }
    } else if (mR && cur) {
      cur.a = (cur.a ? cur.a + '\n' : '') + mR[1].trim()
    } else if (cur) {
      // Continuación de respuesta multilínea
      cur.a = (cur.a ? cur.a + '\n' : '') + line
    }
  }
  if (cur) items.push(cur)
  return items
}

function FAQ({ body }) {
  const items = parseFaqBody(body)
  if (!items.length) return null
  return (
    <div className="my-10 rounded-2xl border border-gray-200 bg-white not-prose">
      <div className="px-5 pt-5 pb-2">
        <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide">Preguntas frecuentes</h3>
      </div>
      <div className="px-5 pb-2">
        {items.map((it, idx) => <FAQItem key={idx} q={it.q} a={it.a} />)}
      </div>
    </div>
  )
}

function Quote({ params, body }) {
  return (
    <figure className="my-10 not-prose">
      <blockquote className="border-l-4 border-blue-500 pl-5 py-1 text-lg text-gray-800 leading-relaxed italic">
        <InlineMarkdown>{body}</InlineMarkdown>
      </blockquote>
      {(params.autor || params.cargo || params.empresa) && (
        <figcaption className="mt-3 text-sm text-gray-500 not-italic">
          <span className="font-semibold text-gray-900">{params.autor}</span>
          {params.cargo && <>, {params.cargo}</>}
          {params.empresa && <> — <span className="text-gray-700">{params.empresa}</span></>}
        </figcaption>
      )}
    </figure>
  )
}

function Estadistica({ params }) {
  return (
    <div className="my-8 rounded-2xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-100 p-6 text-center not-prose">
      <div className="text-4xl sm:text-5xl font-extrabold bg-clip-text text-transparent bg-gradient-to-br from-blue-600 to-indigo-700">
        {params.numero}
      </div>
      <p className="mt-2 text-sm text-gray-700 max-w-md mx-auto">{params.descripcion}</p>
    </div>
  )
}

function Imagen({ params }) {
  return (
    <figure className="my-8 not-prose">
      <img
        src={params.src}
        alt={params.alt}
        loading="lazy"
        className="w-full rounded-2xl border border-gray-100 shadow-sm"
      />
      {params.caption && (
        <figcaption className="mt-2 text-xs text-gray-500 text-center italic">
          {params.caption}
        </figcaption>
      )}
    </figure>
  )
}

function Video({ params }) {
  const [activated, setActivated] = useState(false)
  const id = params.id
  const proveedor = params.proveedor
  const titulo = params.titulo || 'Video'

  // Lite-embed: solo cargamos el iframe al hacer clic, para no penalizar el LCP
  // ni ejecutar trackers de YouTube/Vimeo en cada vista del post.
  if (!activated) {
    const thumbUrl = proveedor === 'youtube'
      ? `https://i.ytimg.com/vi/${id}/hqdefault.jpg`
      : `https://vumbnail.com/${id}.jpg`
    return (
      <div className="my-8 relative aspect-video rounded-2xl overflow-hidden bg-gray-100 not-prose">
        <img src={thumbUrl} alt={titulo} className="absolute inset-0 w-full h-full object-cover" />
        <button
          type="button"
          onClick={() => setActivated(true)}
          className="absolute inset-0 flex items-center justify-center bg-black/30 hover:bg-black/40 transition-colors"
          aria-label={`Reproducir ${titulo}`}
        >
          <span className="flex items-center justify-center w-16 h-16 rounded-full bg-white shadow-lg">
            <FiPlay className="h-7 w-7 text-blue-600 ml-1" />
          </span>
        </button>
      </div>
    )
  }

  const src = proveedor === 'youtube'
    ? `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0`
    : `https://player.vimeo.com/video/${id}?autoplay=1`

  return (
    <div className="my-8 aspect-video rounded-2xl overflow-hidden bg-black not-prose">
      <iframe
        src={src}
        title={titulo}
        className="w-full h-full"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      />
    </div>
  )
}

// Allowlist final de shortcodes — cualquier nombre fuera de aquí se renderiza
// como aviso visible para detectar contenido roto.
const COMPONENTS = {
  cta: CTABox,
  callout: Callout,
  comparativa: Comparativa,
  faq: FAQ,
  quote: Quote,
  estadistica: Estadistica,
  imagen: Imagen,
  video: Video,
}

function ShortcodeFallback({ name }) {
  if (process.env.NODE_ENV === 'production') return null
  return (
    <div className="my-4 p-3 rounded-lg border border-red-300 bg-red-50 text-xs text-red-700 not-prose">
      Shortcode desconocido: <code>:::{name}:::</code>
    </div>
  )
}

// ─── Componente principal ───────────────────────────────────────

export default function MarkdownContent({ contenido }) {
  const blocks = parseContenido(contenido || '')
  return (
    <>
      {blocks.map((b, i) => {
        if (b.type === 'md') {
          return (
            <ReactMarkdown
              key={i}
              remarkPlugins={[remarkGfm]}
              components={mdComponents}
            >
              {b.content}
            </ReactMarkdown>
          )
        }
        const Comp = COMPONENTS[b.name]
        if (!Comp) return <ShortcodeFallback key={i} name={b.name} />
        return <Comp key={i} params={b.params} body={b.body} />
      })}
    </>
  )
}
